import type { ApiClient } from './client';

export interface RankingEntry {
  rank: number;
  userId?: string;        // 본인 행 강조용 (Remote 만 제공)
  companyName: string;
  nickname: string;
  score: number;
  recordedAt: string | null;
}

export const MAX_DAILY_PLAYS = 2;

// 게임 식별 slug. BE GameType.slug 및 localStorage 키 네임스페이스로 함께 쓴다.
export const DEFAULT_GAME_SLUG = 'bloomy-runner';

export interface MyProfile {
  userId: string;           // 본인 식별용 (랭킹 entry.userId 와 매칭)
  nickname: string | null;  // null = 첫 플레이 전
  bestScore: number;
  playCount: number;
  playsRemainingToday: number; // KST 자정 기준 오늘 남은 플레이 횟수
}

export interface SubmitScoreRequest {
  score: number;
}

export interface SubmitScoreResult {
  nickname: string;
  bestScore: number;
  isNewBest: boolean;
  rank: number;
  playCount: number;
  playsRemainingToday: number;
  rankings: RankingEntry[];
}

export interface RankingService {
  fetchTop(limit?: number): Promise<RankingEntry[]>;
  fetchMyProfile(): Promise<MyProfile>;
  submit(request: SubmitScoreRequest): Promise<SubmitScoreResult>;
  // 닉네임만 변경. play_count / best_score 영향 없음.
  updateNickname(nickname: string): Promise<MyProfile>;
}

// ============================================================
// Remote (BeYou BE 연동) — production 기본
// ============================================================

interface RemoteRankingsRs {
  rankings: Array<{
    rank: number;
    userId: string;
    companyName: string;
    nickname: string;
    score: number;
    recordedAt: string | null;
  }>;
}

// BE 응답이 playsRemainingToday 필드를 빠뜨려도(구버전 등) FE 가 NaN/undefined 로
// 망가지지 않도록 응답 레이어에서 한 번에 폴백한다. 기본값은 "오늘은 아직 더 할 수
// 있음" 쪽으로 잡아 사용자 입력이 막히는 일이 없게 한다.
type RawProfile = Omit<MyProfile, 'playsRemainingToday'> & {
  playsRemainingToday?: number;
};
type RawSubmitResult = Omit<SubmitScoreResult, 'playsRemainingToday'> & {
  playsRemainingToday?: number;
};

function normalizeRemaining(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : MAX_DAILY_PLAYS;
}

export class RemoteRankingService implements RankingService {
  // baseUrl 에 이미 `/api` 가 포함되어 있으므로 여기엔 붙이지 않는다.
  private readonly base: string;

  constructor(
    private readonly client: ApiClient,
    slug: string = DEFAULT_GAME_SLUG,
  ) {
    this.base = `/games/${slug}`;
  }

  async fetchTop(limit = 500): Promise<RankingEntry[]> {
    const res = await this.client.get<RemoteRankingsRs>(`${this.base}/rankings?limit=${limit}`);
    return res.rankings;
  }

  async fetchMyProfile(): Promise<MyProfile> {
    const raw = await this.client.get<RawProfile>(`${this.base}/me`);
    return { ...raw, playsRemainingToday: normalizeRemaining(raw.playsRemainingToday) };
  }

  async submit(request: SubmitScoreRequest): Promise<SubmitScoreResult> {
    const raw = await this.client.post<RawSubmitResult, SubmitScoreRequest>(
      `${this.base}/me/score`,
      request,
    );
    return { ...raw, playsRemainingToday: normalizeRemaining(raw.playsRemainingToday) };
  }

  async updateNickname(nickname: string): Promise<MyProfile> {
    const raw = await this.client.patch<RawProfile, { nickname: string }>(
      `${this.base}/me/nickname`,
      { nickname },
    );
    return { ...raw, playsRemainingToday: normalizeRemaining(raw.playsRemainingToday) };
  }
}

// ============================================================
// Local (개발용 fallback) — VITE_API_BASE_URL 미설정 시 사용.
// 인증 없이 단독 동작 가능. 회사명은 'BEYOU' 고정.
// 키는 게임 slug 로 네임스페이스 분리해 게임끼리 localStorage 가 섞이지 않게 한다.
// ============================================================

const STORAGE_KEY = 'bloomy.localRanking';
const PROFILE_KEY = 'bloomy.localProfile';
const SCHEMA_VERSION_KEY = 'bloomy.localSchemaVersion';
const SCHEMA_VERSION = 2;
const MAX_LOCAL_ENTRIES = 500;
const LOCAL_COMPANY_NAME = 'BEYOU';
const LOCAL_USER_ID = 'local-me';
const DAILY_KEY = 'bloomy.localDaily';

// KST(UTC+9) 기준 오늘 날짜 'YYYY-MM-DD'. BE 의 userToday() 와 동일 키 사용.
function kstToday(): string {
  const now = new Date();
  const kstMs = now.getTime() + (9 * 60 - now.getTimezoneOffset()) * 60_000;
  return new Date(kstMs).toISOString().slice(0, 10);
}

interface LocalDailyState {
  date: string;
  playCount: number;
}

function readLocalDaily(dailyKey: string): LocalDailyState {
  const today = kstToday();
  const raw = localStorage.getItem(dailyKey);
  if (!raw) return { date: today, playCount: 0 };
  try {
    const parsed = JSON.parse(raw) as LocalDailyState;
    if (parsed.date !== today) return { date: today, playCount: 0 };
    return { date: today, playCount: Number(parsed.playCount ?? 0) };
  } catch {
    return { date: today, playCount: 0 };
  }
}

function writeLocalDaily(dailyKey: string, next: LocalDailyState): void {
  localStorage.setItem(dailyKey, JSON.stringify(next));
}

function localPlaysRemaining(dailyKey: string): number {
  const { playCount } = readLocalDaily(dailyKey);
  return Math.max(0, MAX_DAILY_PLAYS - playCount);
}

export class LocalRankingService implements RankingService {
  private readonly storageKey: string;
  private readonly profileKey: string;
  private readonly schemaKey: string;
  private readonly dailyKey: string;

  constructor(seed?: ReadonlyArray<RankingEntry>, slug?: string) {
    // slug 미지정 시 기존(게임1) 키를 그대로 사용해 하위호환 유지.
    const suffix = slug ? `.${slug}` : '';
    this.storageKey = STORAGE_KEY + suffix;
    this.profileKey = PROFILE_KEY + suffix;
    this.schemaKey = SCHEMA_VERSION_KEY + suffix;
    this.dailyKey = DAILY_KEY + suffix;

    this.invalidateOldSchema();
    if (seed && seed.length > 0 && this.readEntries().length === 0) {
      const normalized = [...seed]
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_LOCAL_ENTRIES)
        .map((entry, index) => ({ ...entry, rank: index + 1 }));
      localStorage.setItem(this.storageKey, JSON.stringify(normalized));
    }
  }

  async fetchTop(limit = 500): Promise<RankingEntry[]> {
    return this.readEntries().slice(0, limit);
  }

  async fetchMyProfile(): Promise<MyProfile> {
    return this.readProfile();
  }

  async submit({ score }: SubmitScoreRequest): Promise<SubmitScoreResult> {
    const prev = this.readProfile();
    // Local 에서도 BE 와 동일하게 nickname 이 먼저 등록되어 있어야 한다.
    const nickname = prev.nickname;
    if (!nickname) {
      throw new Error('nickname 이 등록되지 않았습니다. 먼저 updateNickname 을 호출하세요.');
    }
    const daily = readLocalDaily(this.dailyKey);
    if (daily.playCount >= MAX_DAILY_PLAYS) {
      throw new Error('오늘은 더 이상 플레이할 수 없어요. 내일 다시 만나요!');
    }
    const nextDaily: LocalDailyState = { date: daily.date, playCount: daily.playCount + 1 };
    writeLocalDaily(this.dailyKey, nextDaily);
    const playsRemainingToday = Math.max(0, MAX_DAILY_PLAYS - nextDaily.playCount);
    const isNewBest = score > prev.bestScore;
    const newProfile: MyProfile = {
      userId: LOCAL_USER_ID,
      nickname,
      bestScore: isNewBest ? score : prev.bestScore,
      playCount: prev.playCount + 1,
      playsRemainingToday,
    };
    localStorage.setItem(this.profileKey, JSON.stringify(newProfile));

    const recordedAt = new Date().toISOString();
    const others = this.readEntries().filter((e) => e.userId !== LOCAL_USER_ID);
    const mine: RankingEntry = {
      rank: 0,
      userId: LOCAL_USER_ID,
      companyName: LOCAL_COMPANY_NAME,
      nickname,
      score: newProfile.bestScore,
      recordedAt: isNewBest ? recordedAt : prev.bestScore > 0 ? null : recordedAt,
    };
    const merged = [...others, mine]
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_LOCAL_ENTRIES)
      .map((entry, index) => ({ ...entry, rank: index + 1 }));
    localStorage.setItem(this.storageKey, JSON.stringify(merged));

    const myRank = merged.find((e) => e.userId === LOCAL_USER_ID)?.rank ?? 1;

    return {
      nickname,
      bestScore: newProfile.bestScore,
      isNewBest,
      rank: myRank,
      playCount: newProfile.playCount,
      playsRemainingToday,
      rankings: merged.slice(0, 500),
    };
  }

  async updateNickname(nickname: string): Promise<MyProfile> {
    const prev = this.readProfile();
    const next: MyProfile = { ...prev, nickname, playsRemainingToday: localPlaysRemaining(this.dailyKey) };
    localStorage.setItem(this.profileKey, JSON.stringify(next));

    // 내 row 가 있으면 닉네임 갱신.
    const entries = this.readEntries();
    const exists = entries.some((entry) => entry.userId === LOCAL_USER_ID);
    const updated = exists
      ? entries.map((entry) =>
          entry.userId === LOCAL_USER_ID ? { ...entry, nickname } : entry,
        )
      : entries;
    localStorage.setItem(this.storageKey, JSON.stringify(updated));
    return next;
  }

  private readEntries(): RankingEntry[] {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (entry): entry is RankingEntry =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as RankingEntry).nickname === 'string' &&
          typeof (entry as RankingEntry).companyName === 'string' &&
          typeof (entry as RankingEntry).score === 'number',
      );
    } catch {
      return [];
    }
  }

  private readProfile(): MyProfile {
    const raw = localStorage.getItem(this.profileKey);
    const playsRemainingToday = localPlaysRemaining(this.dailyKey);
    if (!raw) return { userId: LOCAL_USER_ID, nickname: null, bestScore: 0, playCount: 0, playsRemainingToday };
    try {
      const parsed = JSON.parse(raw) as MyProfile;
      return {
        userId: LOCAL_USER_ID,
        nickname: parsed.nickname ?? null,
        bestScore: Number(parsed.bestScore ?? 0),
        playCount: Number(parsed.playCount ?? 0),
        playsRemainingToday,
      };
    } catch {
      return { userId: LOCAL_USER_ID, nickname: null, bestScore: 0, playCount: 0, playsRemainingToday };
    }
  }

  private invalidateOldSchema(): void {
    const stored = Number(localStorage.getItem(this.schemaKey) ?? 0);
    if (stored !== SCHEMA_VERSION) {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.profileKey);
      localStorage.setItem(this.schemaKey, String(SCHEMA_VERSION));
    }
  }
}
